import { WorkspaceFlavoursService } from '@affine/core/modules/workspace/services/flavours';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { CollectionService } from '@affine/core/modules/collection';
import { AuthService } from '@affine/core/modules/cloud';
import { DocsService } from '@affine/core/modules/doc';
import { DocsSearchService } from '@affine/core/modules/docs-search';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { useLiveData, useService } from '@toeverything/infra';
import { useEffect, useState } from 'react';
import type { ListedBlobRecord } from '@affine/nbstore';
import { Loading, toast, IconButton } from '@affine/component';
import { SettingHeader } from '@affine/component/setting-components';
import bytes from 'bytes';
import dayjs from 'dayjs';
import { getAttachmentFileIcon } from '@blocksuite/affine/components/icons';
import { templateToString } from '@affine/component';
import { DownloadIcon } from '@blocksuite/icons/rc';
import * as styles from './style.css';
import * as cardStyles from './card.css';

type BlobReference = {
  docId: string;
  title: string;
  path: string;
  blockId?: string;
};

const getFieldValue = (value?: string | string[]) => {
  if (Array.isArray(value)) {
    return value[0];
  }
  return value;
};

const formatBlobCreatedAt = (createdAt?: Date | string | number) => {
  if (!createdAt) {
    return '';
  }

  const normalizedCreatedAt =
    typeof createdAt === 'string' && /^\d+$/.test(createdAt)
      ? Number(createdAt)
      : createdAt;

  const parsed = dayjs(normalizedCreatedAt);

  if (!parsed.isValid()) {
    return String(createdAt);
  }

  return parsed.format('YYYY-MM-DD HH:mm:ss');
};

const BlobCard = ({
  blob,
  references,
  onDownload,
  onOpenReference,
}: {
  blob: ListedBlobRecord;
  references: BlobReference[];
  onDownload: (blob: ListedBlobRecord) => void;
  onOpenReference: (reference: BlobReference) => void;
}) => {
  const workspaceService = useService(WorkspaceService);
  const flavoursService = useService(WorkspaceFlavoursService);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const mimeExt = blob.mime ? blob.mime.split('/').pop() : 'unknown';
  const ext = mimeExt === 'plain' ? 'txt' : mimeExt || 'unknown';
  const icon = templateToString(getAttachmentFileIcon(ext));
  
  const isImage = blob.mime?.startsWith('image/');

  useEffect(() => {
    if (!isImage) return;

    let canceled = false;
    let objectUrl: string | null = null;

    const fetchImage = async () => {
      try {
        const flavourProvider = flavoursService.flavours$.value.find(
          f => f.flavour === workspaceService.workspace.flavour
        );
        const data = await flavourProvider?.getWorkspaceBlob(
          workspaceService.workspace.id,
          blob.key
        );
        if (!data || canceled) return;
        
        objectUrl = URL.createObjectURL(new Blob([data], { type: blob.mime }));
        if (!canceled) {
          setImageUrl(objectUrl);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchImage();

    return () => {
      canceled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [blob, flavoursService, workspaceService, isImage]);

  return (
    <div className={cardStyles.blobCard}>
      <div className={cardStyles.actionContainer}>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onDownload(blob);
          }}
          size="20"
          icon={<DownloadIcon />}
          title="下载"
        />
      </div>
      <div className={cardStyles.blobImageContainer}>
        {isImage && imageUrl ? (
          <img src={imageUrl} alt={blob.key} className={cardStyles.blobImage} />
        ) : isImage && !imageUrl ? (
          <Loading size={24} />
        ) : (
          <div
            dangerouslySetInnerHTML={{ __html: icon }}
            className={cardStyles.blobIconContainer}
          />
        )}
      </div>

      <div className={cardStyles.blobFooter}>
        <div className={cardStyles.blobInfo}>
          <span>{formatBlobCreatedAt(blob.createdAt)}</span>
          <span>{bytes(blob.size)}</span>
        </div>
        <div className={cardStyles.referenceSection}>
          <div className={cardStyles.referenceLabel}>被以下文档引用：</div>
          {references.length ? (
            <div className={cardStyles.referenceList}>
              {references.map(reference => (
                <button
                  key={`${blob.key}-${reference.docId}`}
                  className={cardStyles.referenceItem}
                  onClick={event => {
                    event.stopPropagation();
                    onOpenReference(reference);
                  }}
                  type="button"
                  title={reference.path}
                >
                  <span className={cardStyles.referencePath}>{reference.path}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className={cardStyles.referenceEmpty}>暂未发现引用文档</div>
          )}
        </div>
      </div>
    </div>
  );
};

export const WorkspaceSettingMyUploads = ({
  onCloseSetting,
}: {
  onCloseSetting: () => void;
}) => {
  const workspaceService = useService(WorkspaceService);
  const flavoursService = useService(WorkspaceFlavoursService);
  const authService = useService(AuthService);
  const docsSearchService = useService(DocsSearchService);
  const docsService = useService(DocsService);
  const collectionService = useService(CollectionService);
  const workbenchService = useService(WorkbenchService);
  const [blobs, setBlobs] = useState<ListedBlobRecord[]>([]);
  const [blobReferences, setBlobReferences] = useState<Record<string, BlobReference[]>>({});
  const [loading, setLoading] = useState(true);
  const collections = useLiveData(collectionService.collections$);
  const currentAccount = useLiveData(authService.session.account$);

  useEffect(() => {
    let canceled = false;
    const loadBlobs = async () => {
      setLoading(true);
      try {
        const flavourProvider = flavoursService.flavours$.value.find(
          f => f.flavour === workspaceService.workspace.flavour
        );
        const fetchedBlobs = await flavourProvider?.listBlobs(
          workspaceService.workspace.id
        );
        if (!canceled && fetchedBlobs) {
          setBlobs(fetchedBlobs);
        }
      } catch (e) {
        console.error('Failed to list blobs', e);
      } finally {
        if (!canceled) {
          setLoading(false);
        }
      }
    };
    loadBlobs();
    return () => {
      canceled = true;
    };
  }, [flavoursService, workspaceService]);

  useEffect(() => {
    if (!blobs.length) {
      setBlobReferences({});
      return;
    }

    let canceled = false;
    const subscriptions: Array<{ unsubscribe: () => void }> = [];

    const loadReferences = async () => {
      try {
        await workspaceService.workspace.engine.doc.waitForSynced();
        await docsSearchService.indexer.waitForCompleted();

        const blobKeys = blobs.map(blob => blob.key);
        const searchResult = await docsSearchService.indexer.search(
          'block',
          {
            type: 'boolean',
            occur: 'should',
            queries: blobKeys.map(key => ({
              type: 'match',
              field: 'blob',
              match: key,
            })),
          },
          {
            fields: ['blob', 'docId', 'blockId'],
            pagination: {
              skip: 0,
              limit: 5000,
            },
          }
        );

        const refsByBlob = new Map<string, Map<string, BlobReference>>();
        const docIds = new Set<string>();

        for (const node of searchResult.nodes) {
          const blobKey = getFieldValue(node.fields.blob);
          const docId = getFieldValue(node.fields.docId);
          const blockId = getFieldValue(node.fields.blockId);

          if (!blobKey || !docId) {
            continue;
          }

          const title = docsService.list.doc$(docId).value?.title$.value || '未命名文档';
          const reference: BlobReference = {
            docId,
            title,
            path: `全部文档 / ${title}`,
            blockId,
          };

          if (!refsByBlob.has(blobKey)) {
            refsByBlob.set(blobKey, new Map());
          }

          refsByBlob.get(blobKey)?.set(docId, reference);
          docIds.add(docId);
        }

        const collectionDocsMap = new Map<string, string[]>();
        const collectionEntries = Array.from(collections.values());

        await Promise.all(
          collectionEntries.map(
            collection =>
              new Promise<void>(resolve => {
                const subscription = collection.watch().subscribe({
                  next: ids => {
                    collectionDocsMap.set(collection.id, ids);
                    resolve();
                  },
                  error: () => resolve(),
                });
                subscriptions.push(subscription);
              })
          )
        );

        for (const [blobKey, refs] of refsByBlob) {
          for (const reference of refs.values()) {
            const matchedCollection = collectionEntries.find(collection => {
              const ids = collectionDocsMap.get(collection.id) ?? [];
              return ids.includes(reference.docId);
            });

            if (matchedCollection?.info$.value?.name) {
              reference.path = `收藏夹 / ${matchedCollection.info$.value.name} / ${reference.title}`;
            }
          }

          refsByBlob.set(
            blobKey,
            new Map(
              Array.from(refs.entries()).sort(([, a], [, b]) =>
                a.title.localeCompare(b.title, 'zh-CN')
              )
            )
          );
        }

        if (canceled) {
          return;
        }

        setBlobReferences(
          Object.fromEntries(
            blobs.map(blob => [blob.key, Array.from(refsByBlob.get(blob.key)?.values() ?? [])])
          )
        );
      } catch (error) {
        console.error('Failed to load blob references', error);
      }
    };

    void loadReferences();

    return () => {
      canceled = true;
      subscriptions.forEach(subscription => subscription.unsubscribe());
    };
  }, [
    blobs,
    collections,
    docsSearchService,
    docsService,
    workspaceService,
  ]);

  const displayBlobs = currentAccount?.id
    ? blobs.filter(blob => blob.uploadedBy?.includes(currentAccount.id))
    : blobs;

  const handleDownload = async (blob: ListedBlobRecord) => {
    try {
      const flavourProvider = flavoursService.flavours$.value.find(
        f => f.flavour === workspaceService.workspace.flavour
      );
      const data = await flavourProvider?.getWorkspaceBlob(
        workspaceService.workspace.id,
        blob.key
      );
      if (!data) return;
      const url = URL.createObjectURL(new Blob([data], { type: blob.mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = blob.key;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast('下载失败');
    }
  };

  const handleOpenReference = (reference: BlobReference) => {
    if (reference.blockId) {
      workbenchService.workbench.openDoc({
        docId: reference.docId,
        blockIds: [reference.blockId],
      });
    } else {
      workbenchService.workbench.openDoc(reference.docId);
    }

    onCloseSetting();
  };

  return (
    <>
      <SettingHeader
        title="我的上传"
        subtitle="在这里您可以查看所有上传的文档和附件，可下载。"
      />
      <div>
        {loading ? (
          <div className={styles.loadingContainer}>
            <Loading size={32} />
          </div>
        ) : displayBlobs.length === 0 ? (
          <div className={styles.empty}>
            没有找到上传的文档或附件
          </div>
        ) : (
          <div className={styles.list}>
            {displayBlobs.map(blob => (
              <BlobCard
                key={blob.key}
                blob={blob}
                references={blobReferences[blob.key] ?? []}
                onDownload={handleDownload}
                onOpenReference={handleOpenReference}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};
